import React from 'react';
import {
  Mail,
  LinkedinSquare,
  Whatsapp,
  Signal,
  Checkbox as CheckboxIcon,
  GitMerge,
  Linkedin,
  Link as LinkIcon,
} from '@saleshandy/icons';
import { SequenceStepType } from '../../../../sequence/enums';
import { isCallTask, isLinkedInType, isWhatsappTask } from './helper';

const isLinkedInAutomationType = (type: SequenceStepType) =>
  type === SequenceStepType.AimfoxCampaignPush ||
  type === SequenceStepType.HeyReachCampaignPush;

const LightningBoltIcon: React.FC<{ width?: number; height?: number; className?: string }> = ({
  width = 18,
  height = 18,
  className,
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

export interface StepIconProps {
  type: SequenceStepType;
  width?: number;
  height?: number;
  className?: string;
  module?:
    | 'tasks-columns'
    | 'sequence-step'
    | 'sequence-prospect'
    | 'tasker-modal'
    | 'default';
  isHovered?: boolean;
  isSubsequence?: boolean;
}

export interface StepIconConfig {
  icon: React.ComponentType<any>;
  defaultClassName: string;
  hoverClassName?: string;
  subsequenceIcon?: React.ComponentType<any>;
  subsequenceClassName?: string;
}

const getStepIconConfig = (
  type: SequenceStepType,
  module: string,
): StepIconConfig => {
  // LinkedIn Automation types (Aimfox = 19, HeyReach = 20)
  if (isLinkedInAutomationType(type)) {
    switch (module) {
      case 'tasks-columns':
        return {
          icon: LightningBoltIcon,
          defaultClassName: 'gray-txt-9',
        };
      case 'sequence-step':
        return {
          icon: LightningBoltIcon,
          defaultClassName: 'gray-txt-1',
        };
      case 'sequence-prospect':
        return {
          icon: LightningBoltIcon,
          defaultClassName: 'indigo-txt-7',
        };
      case 'tasker-modal':
        return {
          icon: LightningBoltIcon,
          defaultClassName: 'gray-txt-1',
        };
      default:
        return {
          icon: LightningBoltIcon,
          defaultClassName: 'gray-txt-1',
        };
    }
  }

  // LinkedIn types
  if (isLinkedInType(type)) {
    switch (module) {
      case 'tasks-columns':
        return {
          icon: LinkedinSquare,
          defaultClassName: 'gray-txt-11 hr-blue',
        };
      case 'sequence-step':
        return {
          icon: Linkedin,
          defaultClassName: 'gray-txt-1',
        };
      case 'sequence-prospect':
        return {
          icon: Linkedin,
          defaultClassName: 'blue-txt-29',
        };
      case 'tasker-modal':
        return {
          icon: Linkedin,
          defaultClassName: 'gray-txt-1',
        };
      default:
        return {
          icon: Linkedin,
          defaultClassName: 'gray-txt-1',
        };
    }
  }

  // WhatsApp
  if (isWhatsappTask(type)) {
    switch (module) {
      case 'tasks-columns':
        return {
          icon: Whatsapp,
          defaultClassName: 'gray-txt-11 hr-green',
        };
      case 'sequence-step':
        return {
          icon: Whatsapp,
          defaultClassName: 'gray-txt-1',
        };
      case 'sequence-prospect':
        return {
          icon: Whatsapp,
          defaultClassName: 'green-txt-25',
        };
      case 'tasker-modal':
        return {
          icon: Whatsapp,
          defaultClassName: 'gray-txt-1',
        };
      default:
        return {
          icon: Whatsapp,
          defaultClassName: 'gray-txt-1',
        };
    }
  }

  // Call tasks
  if (isCallTask(type)) {
    switch (module) {
      case 'tasks-columns':
        return {
          icon: Signal,
          defaultClassName: 'gray-txt-11 hr-purple',
        };
      case 'sequence-step':
        return {
          icon: Signal,
          defaultClassName: 'gray-txt-1',
        };
      case 'sequence-prospect':
        return {
          icon: Signal,
          defaultClassName: 'purple-txt-16',
        };
      case 'tasker-modal':
        return {
          icon: Signal,
          defaultClassName: 'gray-txt-1',
        };
      default:
        return {
          icon: Signal,
          defaultClassName: 'gray-txt-1',
        };
    }
  }

  // Email
  if (type === SequenceStepType.Email) {
    switch (module) {
      case 'tasks-columns':
        return {
          icon: Mail,
          defaultClassName: 'gray-txt-11',
        };
      case 'sequence-step':
        return {
          icon: Mail,
          defaultClassName: 'gray-txt-1',
        };
      case 'sequence-prospect':
        return {
          icon: Mail,
          defaultClassName: 'blue-txt-24',
        };
      case 'tasker-modal':
        return {
          icon: Mail,
          defaultClassName: 'gray-txt-1',
        };
      default:
        return {
          icon: Mail,
          defaultClassName: 'gray-txt-1',
        };
    }
  }

  // Custom/Checkbox (default)
  switch (module) {
    case 'tasks-columns':
      return {
        icon: CheckboxIcon,
        defaultClassName: 'gray-txt-11 hr-orange',
      };
    case 'sequence-step':
      return {
        icon: CheckboxIcon,
        defaultClassName: 'gray-txt-1',
      };
    case 'sequence-prospect':
      return {
        icon: CheckboxIcon,
        defaultClassName: 'orange-txt-21',
      };
    case 'tasker-modal':
      return {
        icon: CheckboxIcon,
        defaultClassName: 'gray-txt-1',
      };
    default:
      return {
        icon: CheckboxIcon,
        defaultClassName: 'gray-txt-1',
      };
  }
};

export const getStepIcon = ({
  type,
  width,
  height,
  className,
  module = 'default',
  isHovered = false,
  isSubsequence = false,
}: StepIconProps): React.ReactElement => {
  // Handle subsequence case
  if (isSubsequence) {
    return (
      <GitMerge
        width={width}
        height={height}
        className={className || 'purple-txt-16'}
      />
    );
  }

  const config = getStepIconConfig(type, module);
  const IconComponent = config.icon;

  let finalClassName = className;
  if (!finalClassName) {
    if (isHovered && config.hoverClassName) {
      finalClassName = config.hoverClassName;
    } else {
      finalClassName = config.defaultClassName;
    }
  }

  return (
    <IconComponent width={width} height={height} className={finalClassName} />
  );
};
